import DatePicker from '@mui/lab/DatePicker';
import MobileDatePicker from '@mui/lab/MobileDatePicker';
import { PickersDayProps } from '@mui/lab/PickersDay';
import StaticDatePicker from '@mui/lab/StaticDatePicker';
import { useTranslation } from 'react-i18next';
import { portalLanguage } from '../i18n';
import styles from '../sass/components/DatePicker.module.scss';
import TextInput from './TextInput';

interface DatePickerProps {
  label: string;
  value: Date | null | undefined;
  id: string;
  onChange: (date: Date | null) => void;
  onMonthChange?: (date: Date) => void;
  disabled?: boolean;
  minDate?: Date;
  /** Last day that can be picked (the customer booking horizon). */
  maxDate?: Date;
  disablePast?: boolean;
  loading?: boolean;
  popout?: boolean;
  open?: boolean;
  mobile?: boolean;
  renderDay?: ((
    day: Date,
    selectedDates: (Date | null)[],
    pickersDayProps: PickersDayProps<Date>) => JSX.Element) | undefined
}

const Toolbar = () => <div />;
export default function Picker({
  label,
  value,
  onChange,
  id,
  minDate,
  maxDate,
  disablePast,
  renderDay,
  disabled,
  onMonthChange,
  loading,
  popout = true,
  mobile = false,
  open,
}: DatePickerProps) {
  const { t } = useTranslation();
  // The pickers' own words (month arrows, the phone dialog's buttons).
  const lang = portalLanguage();
  const pickerText = {
    leftArrowButtonText: t('datePicker.previousMonth'),
    rightArrowButtonText: t('datePicker.nextMonth'),
    // The field's own format: the locale default would print "26-09-28"
    // for fr-CA. English passes nothing, as before.
    ...(lang === 'fr' && { inputFormat: 'yyyy-MM-dd' }),
    ...(lang === 'es' && { inputFormat: 'dd/MM/yyyy' }),
  };
  if (!popout && !mobile) {
    return (
      <StaticDatePicker
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...pickerText}
        label={label}
        value={value}
        onChange={onChange}
        onMonthChange={onMonthChange}
        minDate={minDate}
        maxDate={maxDate}
        disablePast={disablePast}
        renderDay={renderDay}
        open
        ToolbarComponent={Toolbar}
        disabled={disabled}
        loading={loading}
        renderInput={() => (
          <input />
        )}
      />
    );
  }
  if (mobile) {
    return (
      <MobileDatePicker
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...pickerText}
        okText={t('datePicker.ok')}
        cancelText={t('datePicker.cancel')}
        label={label}
        value={value}
        onChange={onChange}
        onMonthChange={onMonthChange}
        minDate={minDate}
        maxDate={maxDate}
        disablePast={disablePast}
        renderDay={renderDay}
        open={open}
        ToolbarComponent={Toolbar}
        disabled={disabled}
        loading={loading}
        renderInput={({ inputRef, inputProps }) => (
          <div className={styles.wrapper}>
            <TextInput
              ref={inputRef}
              id={`date-picker-${id}`}
              title={label}
              defaultValue={value}
              onChange={() => undefined}
            // eslint-disable-next-line react/jsx-props-no-spreading
              {...inputProps}
            />
          </div>
        )}
      />
    );
  }
  return (
    <DatePicker
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...pickerText}
      label={label}
      value={value}
      onChange={onChange}
      onMonthChange={onMonthChange}
      minDate={minDate}
      maxDate={maxDate}
      disablePast={disablePast}
      renderDay={renderDay}
      open={open}
      ToolbarComponent={Toolbar}
      disabled={disabled}
      loading={loading}
      renderInput={({ inputRef, inputProps, InputProps }) => (
        <div className={styles.wrapper}>
          <TextInput
            ref={inputRef}
            id={`date-picker-${id}`}
            title={label}
            rightIcon={InputProps?.endAdornment}
            defaultValue={value}
            onChange={() => undefined}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...inputProps}
          />
        </div>
      )}
    />
  );
}
